import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Checkbox,
  Chip,
  CircularProgress,
  Paper,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

/**
 * One-time "Ingram Mapping" admin tool.
 *
 * The heavy Ingram sweep is run OFFLINE (node scripts/ingramDiscoverAndMap.js),
 * which stages results in ingram_mapping_candidates. This page reads them:
 *   - Plans: distinct VPNs in use + whether each is already wired in admin (the
 *     UNWIRED ones are your to-do list to add under Ingram mappings).
 *   - Proposed matches: tick the ones you trust, then "Sync selected" writes
 *     ingram_subscription_id onto the sub + the customer name onto the workspace.
 *   - Flagged: Ingram subs we couldn't confidently place (handle manually).
 */
// What each flag actually means, so a row can be acted on without reading the
// script. Shown as the Reason tooltip.
const FLAG_HELP = {
  already_linked_in_db: "This Ingram subscription id is already stored on one of our subscriptions — nothing to do.",
  no_workspace_for_customer:
    "No workspace matched this Ingram customer's legal name. They may not be on our app, or the name differs too much to match.",
  wired_plan_no_unlinked_sub_on_mapped_price:
    "The plan IS wired in admin, but this customer has no unlinked subscription on that mapped price. Check the price the customer actually bought.",
  ambiguous_on_mapped_price:
    "Several unlinked subscriptions of this customer sit on the mapped price and the quantity could not separate them. Link this one by hand.",
  ambiguous_plan_match:
    "Several of this customer's subscriptions match the plan name and the quantity could not separate them. Link by hand.",
  no_plan_match_in_workspace:
    "The customer's workspace was found, but none of their subscriptions match this plan name.",
};

/**
 * One flagged row's detail. Used by BOTH flagged tables so they can never drift
 * apart.
 */
/**
 * Exactly what to write in the DB to link one Ingram subscription by hand.
 * Linking is only ever two writes — the same two "Sync selected" performs — so
 * doing it manually is safe as long as both are done.
 */
function LinkHowTo({ row, onClose }) {
  if (!row) return null;
  const sid = row.ingram_subscription_id;
  const cust = String(row.ingram_customer_name || "").replace(/'/g, "''");
  const sql = `-- 1) Link the subscription. Replace <OUR_SUB_ID> with the id from purchasesubscriptions.
UPDATE purchasesubscriptions
   SET ingram_subscription_id = '${sid}'
 WHERE id = <OUR_SUB_ID>
   AND ingram_subscription_id IS NULL;

-- 2) Store Ingram's END CUSTOMER company name for that workspace.
--    VERIFY this against the "End customer" column on the Ingram portal first.
--    The value below is whatever Ingram put on the ORDER, which is sometimes a
--    person's name rather than the company. It must be the COMPANY name, exactly
--    as the portal spells it, or the next order creates a duplicate end customer.
UPDATE workspaces
   SET ingram_customer_name = '${cust}'
 WHERE id = <WORK_ID>
   AND (ingram_customer_name IS NULL OR ingram_customer_name = '');

-- Find the candidate subscriptions for that customer:
-- SELECT ps.id, ps.work_id, ps.price_id, ps.skuName, ps.noOfLicences, ps.interval
-- FROM purchasesubscriptions ps
-- JOIN workspaces w ON w.id = ps.work_id
-- WHERE ps.ingram_subscription_id IS NULL AND w.company LIKE '%${cust.slice(0, 18)}%';`;
  return (
    <Dialog open={!!row} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 0 }}>
        Link this subscription by hand
        <Typography variant="body2" color="text.secondary">
          {row.ingram_customer_name} · {row.plan_name}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Table size="small" sx={{ mb: 2 }}>
          <TableBody>
            {[
              ["Ingram subscription id", sid],
              ["Ingram customer", row.ingram_customer_name],
              ["Ingram contact", row.ingram_contact],
              ["Plan", row.plan_name],
              ["Plan ID", row.plan_id],
              ["VPN", row.vpn],
              ["Ingram term", row.plan_term],
              ["Period ends", row.ingram_end_date],
              ["Portal status", row.portal_status],
              ["Portal licences", row.portal_licenses],
              ["Our workspace id", row.work_id],
              ["Our workspace", row.ws_company],
              ["Workspace ingram_customer_name", row.ws_ingram_customer_name],
            ].map(([k, v]) => (
              <TableRow key={k}>
                <TableCell sx={{ width: 220, fontWeight: 500, borderBottom: "none" }}>{k}</TableCell>
                <TableCell sx={{ fontFamily: "monospace", borderBottom: "none" }}>{v || "— unknown —"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Alert severity="info" sx={{ mb: 2 }}>
          Two writes, nothing else. <strong>purchasesubscriptions.ingram_subscription_id</strong> on the matching
          subscription, and <strong>workspaces.ingram_customer_name</strong> on its workspace. Orders, invoices and
          Stripe are never touched. Once written, this row disappears from the screen on the next{" "}
          <strong>Refresh view</strong> — no re-sweep needed.
        </Alert>
        {row.ws_name_matches ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            The workspace already stores this exact Ingram customer name — <strong>skip write #2</strong>, only the
            subscription link is needed.
          </Alert>
        ) : null}
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>Check the customer name before running this.</strong> The value below is what Ingram put on the
          order, which is sometimes a contact&apos;s name rather than the company. It must be the{" "}
          <strong>End customer company name exactly as the Ingram portal shows it</strong> — it becomes{" "}
          <code>endUserInfo.companyName</code> on every future order for this workspace, so a wrong value creates a
          duplicate end customer on Ingram instead of matching the existing one.
        </Alert>
        <Box
          component="pre"
          sx={{ p: 1.5, bgcolor: "#f6f8fa", borderRadius: 1, fontSize: 12, whiteSpace: "pre-wrap", wordBreak: "break-word" }}
        >
          {sql}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => navigator.clipboard && navigator.clipboard.writeText(sql)}>Copy SQL</Button>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

function FlaggedTable({ rows, onLink }) {
  return (
    <Paper variant="outlined" sx={{ overflowX: "auto", mb: 3 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Customer</TableCell>
            <TableCell>Our workspace</TableCell>
            <TableCell>Plan (from Ingram)</TableCell>
            <TableCell>Plan ID</TableCell>
            <TableCell>Ingram sub id</TableCell>
            <TableCell>VPN</TableCell>
            <TableCell align="right">
              <Tooltip title="Seats on Ingram ORDER lines, not the live seat count. Renewals report 0.">
                <span>Ingram order qty</span>
              </Tooltip>
            </TableCell>
            <TableCell>Ingram term</TableCell>
            <TableCell>Reason</TableCell>
            <TableCell align="center">Link</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.id}>
              <TableCell>
                {r.ingram_customer_name || "—"}
                {r.ingram_contact && r.ingram_contact !== r.ingram_customer_name ? (
                  <Typography variant="caption" color="text.secondary" display="block">
                    contact: {r.ingram_contact}
                  </Typography>
                ) : null}
              </TableCell>
              <TableCell>
                {r.work_id ? (
                  <>
                    {r.ws_company || `#${r.work_id}`}
                    {r.ws_ingram_customer_name ? (
                      <Typography
                        variant="caption"
                        display="block"
                        sx={{ color: r.ws_name_matches ? "success.main" : "warning.main" }}
                      >
                        {r.ws_name_matches ? "✓ already set: " : "⚠ set to: "}
                        {r.ws_ingram_customer_name}
                      </Typography>
                    ) : (
                      <Typography variant="caption" color="text.secondary" display="block">
                        no Ingram name stored yet
                      </Typography>
                    )}
                  </>
                ) : (
                  "—"
                )}
              </TableCell>
              <TableCell>{r.plan_name || "—"}</TableCell>
              <TableCell sx={{ fontFamily: "monospace" }}>{r.plan_id || "—"}</TableCell>
              <TableCell sx={{ fontFamily: "monospace" }}>
                {r.ingram_subscription_id}
                {r.portal_status ? (
                  <Typography
                    variant="caption"
                    display="block"
                    sx={{ fontFamily: "inherit", color: r.portal_is_active ? "success.main" : "error.main" }}
                  >
                    {r.portal_status}
                  </Typography>
                ) : null}
              </TableCell>
              <TableCell sx={{ fontFamily: "monospace" }}>{r.vpn || "—"}</TableCell>
              <TableCell align="right">{Number(r.ingram_qty) > 0 ? r.ingram_qty : "—"}</TableCell>
              <TableCell>
                {r.plan_term || "—"}
                {r.ingram_end_date ? (
                  <Typography variant="caption" color="text.secondary" display="block">
                    ends {r.ingram_end_date}
                  </Typography>
                ) : null}
              </TableCell>
              <TableCell>
                <Tooltip title={FLAG_HELP[r.flag] || r.flag}>
                  <Chip size="small" variant="outlined" label={r.flag} />
                </Tooltip>
              </TableCell>
              <TableCell align="center">
                <Tooltip title="Show exactly what to write in the DB to link this one by hand">
                  <IconButton size="small" onClick={() => onLink && onLink(r)}>
                    <InfoOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
          {!rows.length ? (
            <TableRow>
              <TableCell colSpan={10} align="center" sx={{ color: "text.secondary" }}>
                None.
              </TableCell>
            </TableRow>
          ) : null}
        </TableBody>
      </Table>
    </Paper>
  );
}

export default function IngramMapping() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [proposed, setProposed] = useState([]);
  const [flagged, setFlagged] = useState([]);
  const [flaggedBulk, setFlaggedBulk] = useState([]);
  const [bulkPlanIds, setBulkPlanIds] = useState([]);
  const [flaggedBulkKnown, setFlaggedBulkKnown] = useState([]);
  const [bulkListSize, setBulkListSize] = useState(0);
  const [bulkKnownCompanies, setBulkKnownCompanies] = useState(0);
  const [bulkDuplicates, setBulkDuplicates] = useState([]);
  const [hiddenNotLive, setHiddenNotLive] = useState(0);
  const [bulkMissingDetail, setBulkMissingDetail] = useState([]);
  const [linkRow, setLinkRow] = useState(null);
  const [diag, setDiag] = useState(null);
  const [plans, setPlans] = useState([]);
  const [syncedCount, setSyncedCount] = useState(0);
  const [selected, setSelected] = useState(() => new Set());
  const [infoPlan, setInfoPlan] = useState(null);
  const [rechecking, setRechecking] = useState(false);
  const [run, setRun] = useState(null);
  const [runErr, setRunErr] = useState("");

  // "Refresh" re-reads the page. Re-checking wired is what people actually expect
  // from it after wiring prices, so do that first and then reload.
  const refresh = useCallback(async () => {
    try {
      await fetch(`${process.env.REACT_APP_SERVER_URL}/admin/ingram/recheck-wired`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });
    } catch (_e) {
      /* non-fatal — still reload below */
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const res = await fetch(`${process.env.REACT_APP_SERVER_URL}/admin/ingram/candidates`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });
      if (res.status === 201 || res.status === 202 || res.status === 401) {
        navigate("/login");
        return;
      }
      const data = await res.json();
      if (data && data.success) {
        setProposed(data.data.proposed || []);
        setFlagged(data.data.flagged || []);
        setFlaggedBulk(data.data.flaggedBulk || []);
        setBulkPlanIds(data.data.bulkPlanIds || []);
        setFlaggedBulkKnown(data.data.flaggedBulkKnown || []);
        setBulkListSize(data.data.bulkListSize || 0);
        setBulkKnownCompanies(data.data.bulkKnownCompanies || 0);
        setBulkDuplicates(data.data.bulkDuplicates || []);
        setHiddenNotLive(data.data.hiddenNotLive || 0);
        setBulkMissingDetail(data.data.bulkMissingDetail || []);
        setPlans(data.data.plans || []);
        setSyncedCount(data.data.syncedCount || 0);
        setSelected(new Set());
      } else {
        setErr((data && data.msg) || "Failed to load candidates.");
      }
    } catch (e) {
      setErr(e.message || "Failed to load candidates.");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const running = !!run && run.status === "running";

  // Poll while a sweep is in flight, and reload the page data once it lands.
  useEffect(() => {
    if (!running) return undefined;
    let stop = false;
    const id = setInterval(async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_SERVER_URL}/admin/ingram/discover/status`, {
          method: "GET",
          credentials: "include",
        });
        const data = await res.json();
        if (stop || !data || !data.run) return;
        setRun(data.run);
        if (data.run.status === "done") {
          setMsg(
            `Sweep finished: ${data.run.summary ? `${data.run.summary.ordersSwept} orders, ${data.run.summary.ingramSubs} subscriptions, ${data.run.summary.plans} plans, ${data.run.summary.matches} proposed` : "done"}.`
          );
          await load();
        }
      } catch (_e) {
        /* transient — keep polling */
      }
    }, 5000);
    return () => {
      stop = true;
      clearInterval(id);
    };
  }, [running, load]);

  // On mount, adopt any sweep already running (e.g. started before a reload).
  // Failures used to be swallowed, which looked identical to "nothing running" —
  // surface them instead so a misconfigured proxy is obvious.
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_SERVER_URL}/admin/ingram/discover/status`, {
          method: "GET",
          credentials: "include",
        });
        const data = await res.json();
        if (data && data.run) {
          if (data.run.status !== "idle") setRun(data.run);
        } else {
          setRunErr((data && data.msg) || `Sweep status unavailable (HTTP ${res.status}).`);
        }
      } catch (e) {
        setRunErr(e.message || "Sweep status unavailable.");
      }
    })();
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    setSelected((prev) => (prev.size === proposed.length ? new Set() : new Set(proposed.map((p) => p.id))));
  };

  const sync = async () => {
    if (!selected.size) return;
    setSyncing(true);
    setErr("");
    setMsg("");
    try {
      const res = await fetch(`${process.env.REACT_APP_SERVER_URL}/admin/ingram/sync`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ ids: [...selected] }),
      });
      const data = await res.json();
      if (data && data.success) {
        setMsg(`Synced ${data.synced} subscription id(s).`);
        await load();
      } else {
        setErr((data && data.msg) || "Sync failed.");
      }
    } catch (e) {
      setErr(e.message || "Sync failed.");
    } finally {
      setSyncing(false);
    }
  };

  // Fast, DB-only: recompute the "Wired?" column against ingram_product_maps.
  // Use this after wiring prices — no Ingram call, no full sweep.
  const recheckWired = async () => {
    setRechecking(true);
    setErr("");
    setMsg("");
    try {
      const res = await fetch(`${process.env.REACT_APP_SERVER_URL}/admin/ingram/recheck-wired`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });
      const data = await res.json();
      if (data && data.success) {
        setMsg(`Re-checked against ${data.mappings} active mapping(s): ${data.wired} of ${data.rows} row(s) wired (${data.changed} changed).`);
        setDiag(data);
        await load();
      } else {
        setErr((data && data.msg) || "Re-check failed.");
      }
    } catch (e) {
      setErr(e.message || "Re-check failed.");
    } finally {
      setRechecking(false);
    }
  };

  // Full sweep of every Ingram order. Staging only — never binds subscriptions.
  const startDiscovery = async () => {
    setErr("");
    setMsg("");
    try {
      const res = await fetch(`${process.env.REACT_APP_SERVER_URL}/admin/ingram/discover`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ apply: false }),
      });
      const data = await res.json();
      if (data && data.success) {
        setRun(data.run || { status: "running" });
        setMsg("Full Ingram sweep started. This takes a few minutes — the page will update when it finishes.");
      } else {
        setErr((data && data.msg) || "Could not start the sweep.");
        if (data && data.run) setRun(data.run);
      }
    } catch (e) {
      setErr(e.message || "Could not start the sweep.");
    }
  };

  const unwired = plans.filter((p) => !p.wired);

  if (loading) {
    return (
      <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h5">Ingram Mapping</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button variant="outlined" onClick={async () => { await refresh(); await load(); }} disabled={running}>
            Refresh view
          </Button>
          <Tooltip title="Re-checks the Wired? column against our own mappings. No Ingram call — instant.">
            <span>
              <Button variant="outlined" onClick={recheckWired} disabled={rechecking || running}>
                {rechecking ? <CircularProgress size={20} /> : "Re-check wired"}
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Sweeps EVERY Ingram order and rebuilds this page. Takes a few minutes. Never binds subscriptions on its own.">
            <span>
              <Button variant="contained" onClick={startDiscovery} disabled={running}>
                {running ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Full sync from Ingram"}
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        <strong>Full sync from Ingram</strong> re-sweeps every Ingram order and rebuilds this page.{" "}
        <strong>Re-check wired</strong> only re-reads our own mappings, so use it after you wire prices.{" "}
        <strong>Refresh view</strong> just re-reads this page. <em>Ingram order qty</em> is the quantity on Ingram
        order lines, not the live seat count (renewals report 0) — verify seats in the portal.{" "}
        None of them touch orders — syncing only writes{" "}
        <code>ingram_subscription_id</code> on the sub + the customer name on the workspace, and only for the rows you tick.
        {syncedCount ? `  ${syncedCount} already synced.` : ""}
      </Typography>

      {running ? (
        <Alert severity="info" sx={{ mb: 2 }} icon={<CircularProgress size={18} />}>
          Sweeping Ingram… {run && run.elapsedMs ? `${Math.round(run.elapsedMs / 1000)}s elapsed. ` : ""}
          Safe to leave this page open; it refreshes automatically when the sweep finishes.
        </Alert>
      ) : null}
      {run && run.status === "error" ? (
        <Alert severity="error" sx={{ mb: 2 }}>Sweep failed: {run.error}</Alert>
      ) : null}
      {hiddenNotLive ? (
        <Alert severity="success" sx={{ mb: 2 }}>
          {hiddenNotLive} subscription(s) hidden — the Ingram portal reports them as terminated. Re-run{" "}
          <code>node scripts/ingramPortalSubscriptionSweep.js</code> to refresh those statuses.
        </Alert>
      ) : null}
      {runErr ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Could not read sweep status: {runErr} — check <code>SPEND_BACKEND_URL</code> and{" "}
          <code>INTERNAL_OPS_SECRET</code> in admin_backend.
        </Alert>
      ) : null}
      {!running && run && run.status === "done" ? (
        <Alert severity="success" sx={{ mb: 2 }}>
          Last sweep finished {run.finishedAt ? new Date(run.finishedAt).toLocaleString() : ""}
          {run.summary
            ? ` — ${run.summary.ordersSwept} orders, ${run.summary.ingramSubs} subscriptions, ${run.summary.plans} plans, ${run.summary.matches} proposed, ${run.summary.flagged} flagged.`
            : "."}
        </Alert>
      ) : null}

      {diag && (diag.unmatched || []).length ? (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setDiag(null)}>
          <strong>Why some plans still show as not wired.</strong> The check compares plan_id, and for Google the plan
          name decoded from the mapping&apos;s <code>product_url</code>, against the plan name Ingram puts on the order
          line. They have to be the same string.
          <div style={{ marginTop: 8 }}>
            <strong>Active mappings offer:</strong>
            <ul style={{ margin: "4px 0", paddingLeft: 20 }}>
              {(diag.mappingKeys || []).map((m) => (
                <li key={m.price_id}>
                  price {m.price_id} ({m.provider}) — plan_id <code>{m.plan_id || "—"}</code>
                  {m.url_plan ? (
                    <>
                      , url plan <code>{m.url_plan}</code>
                    </>
                  ) : null}
                </li>
              ))}
              {!(diag.mappingKeys || []).length ? <li>none — no active, complete mapping exists</li> : null}
            </ul>
            <strong>Unwired plans expect:</strong>
            <ul style={{ margin: "4px 0", paddingLeft: 20 }}>
              {(diag.unmatched || []).slice(0, 25).map((u) => (
                <li key={u.plan_name}>
                  <code>{u.plan_name}</code> ({u.provider}) — plan_id <code>{u.looked_for_plan_id || "—"}</code>
                </li>
              ))}
            </ul>
            {(diag.skipped || []).length ? (
              <>
                <strong>Mappings ignored:</strong>
                <ul style={{ margin: "4px 0", paddingLeft: 20 }}>
                  {diag.skipped.map((m, i) => (
                    <li key={i}>
                      price {m.price_id} ({m.provider}) — {m.reason}
                    </li>
                  ))}
                </ul>
              </>
            ) : null}
          </div>
        </Alert>
      ) : null}
      {err ? <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert> : null}
      {msg ? <Alert severity="success" sx={{ mb: 2 }}>{msg}</Alert> : null}

      {/* ---- Plans to wire ---- */}
      <Typography variant="h6" sx={{ mt: 1, mb: 1 }}>
        Plans in use ({plans.length}) — {unwired.length} not yet wired in admin
      </Typography>
      <Paper variant="outlined" sx={{ mb: 3, overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Plan (from Ingram)</TableCell>
              <TableCell>Plan ID</TableCell>
              <TableCell>VPN</TableCell>
              <TableCell align="right">Subs</TableCell>
              <TableCell align="right">Customers</TableCell>
              <TableCell>Wired?</TableCell>
              <TableCell align="center">Mapping</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {plans.map((p, i) => (
              <TableRow key={p.plan_name || p.vpn || i} sx={p.wired ? {} : { backgroundColor: "rgba(255,193,7,0.12)" }}>
                <TableCell>{p.plan_name || "—"}</TableCell>
                <TableCell sx={{ fontFamily: "monospace" }}>{p.plan_id || "—"}</TableCell>
                <TableCell sx={{ fontFamily: "monospace" }}>{p.vpn || "—"}</TableCell>
                <TableCell align="right">{p.subscriptions}</TableCell>
                <TableCell align="right">{p.customers}</TableCell>
                <TableCell>
                  {p.wired ? <Chip size="small" color="success" label="wired" /> : <Chip size="small" color="warning" label="ADD in admin" />}
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="Show the exact values to paste into this product's Ingram auto-fulfilment form">
                    <IconButton size="small" onClick={() => setInfoPlan(p)}>
                      <InfoOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      {/* ---- Proposed matches ---- */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
        <Typography variant="h6">Proposed subscription matches ({proposed.length})</Typography>
        <Button variant="contained" disabled={!selected.size || syncing} onClick={sync}>
          {syncing ? <CircularProgress size={20} /> : `Sync selected (${selected.size})`}
        </Button>
      </Box>
      <Paper variant="outlined" sx={{ mb: 3, overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={proposed.length > 0 && selected.size === proposed.length}
                  indeterminate={selected.size > 0 && selected.size < proposed.length}
                  onChange={toggleAll}
                />
              </TableCell>
              <TableCell>Customer</TableCell>
              <TableCell>Our sub</TableCell>
              <TableCell>SKU</TableCell>
              <TableCell align="right">Our qty</TableCell>
              <TableCell align="right">
                <Tooltip title="Seats on Ingram ORDER lines, not the live subscription seat count. Renewal orders report 0, so this is often blank. Ingram's Subscription Management API is not provisioned on our credentials, so the live count cannot be read — check the portal.">
                  <span>Ingram order qty</span>
                </Tooltip>
              </TableCell>
              <TableCell>Ingram sub id</TableCell>
              <TableCell>VPN</TableCell>
              <TableCell>Our billing</TableCell>
              <TableCell>Our commitment</TableCell>
              <TableCell>Ingram term</TableCell>
              <TableCell>Match</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {proposed.map((r) => (
              <TableRow key={r.id} hover selected={selected.has(r.id)}>
                <TableCell padding="checkbox">
                  <Checkbox checked={selected.has(r.id)} onChange={() => toggle(r.id)} />
                </TableCell>
                <TableCell>{r.ingram_customer_name}</TableCell>
                <TableCell>#{r.purchase_subscription_id}</TableCell>
                <TableCell>{r.our_sku}</TableCell>
                <TableCell align="right">{r.our_qty}</TableCell>
                <TableCell align="right">{Number(r.ingram_qty) > 0 ? r.ingram_qty : "—"}</TableCell>
                <TableCell sx={{ fontFamily: "monospace" }}>{r.ingram_subscription_id}</TableCell>
                <TableCell sx={{ fontFamily: "monospace" }}>{r.vpn}</TableCell>
                <TableCell>{r.our_interval || "—"}</TableCell>
                <TableCell>{r.our_validity || "—"}</TableCell>
                <TableCell>
                  {r.plan_term || "—"}
                  {r.term_match === "MISMATCH" ? (
                    <Tooltip title="Our commitment term does not equal the Ingram plan's term. Term is NOT a matching criterion — verify this row before syncing.">
                      <Chip size="small" color="warning" label="term differs" sx={{ ml: 0.5 }} />
                    </Tooltip>
                  ) : null}
                </TableCell>
                <TableCell>
                  <Chip size="small" label={r.match_reason} />
                </TableCell>
              </TableRow>
            ))}
            {!proposed.length ? (
              <TableRow>
                <TableCell colSpan={12} align="center" sx={{ color: "text.secondary" }}>
                  No proposed matches. Run the discovery script on the server.
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </Paper>

      {/* ---- Flagged: needs review ---- */}
      <Typography variant="h6" sx={{ mb: 1 }}>
        Flagged — needs review ({flagged.length})
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        On Ingram with no confident match, excluding the bulk same-plan groups below. This is the list worth working
        through.
      </Typography>
      <FlaggedTable rows={flagged} onLink={setLinkRow} />

      {/* ---- Flagged: bulk same-plan groups ---- */}
      <Typography variant="h6" sx={{ mb: 1 }}>
        Flagged — No Teams monthly, NOT on the known company list ({flaggedBulk.length})
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Plan {(bulkPlanIds || []).join(", ")} — Business Basic (No Teams) (NCE COM MTH), one licence — but the customer
        is <strong>not</strong> one of the {bulkListSize} companies on the bulk list. This is the short list to work
        through by hand.
      </Typography>
      <FlaggedTable rows={flaggedBulk} onLink={setLinkRow} />

      {/* ---- Flagged: the known ~200 per-domain companies ---- */}
      <Typography variant="h6" sx={{ mb: 1 }}>
        Flagged — known bulk companies ({flaggedBulkKnown.length})
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Business Basic (No Teams) (NCE COM MTH) where the customer name is an exact match for one of the {bulkListSize}{" "}
        companies on the list — the one client&apos;s per-domain orders. Nothing to do here for now.
        {bulkKnownCompanies
          ? ` Each row is one Ingram SUBSCRIPTION, not one company: ${flaggedBulkKnown.length} subscriptions owned by ${bulkKnownCompanies} of the ${bulkListSize} companies.`
          : ""}
      </Typography>
      {bulkDuplicates.length ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          {bulkKnownCompanies} companies own {flaggedBulkKnown.length} subscriptions because{" "}
          {bulkDuplicates.length} of them have more than one:
          <ul style={{ margin: "6px 0 0", paddingLeft: 20 }}>
            {bulkDuplicates.map((d) => (
              <li key={d.name}>
                <strong>{d.name}</strong> — {d.count} subscriptions (
                {d.subs.join(", ")})
              </li>
            ))}
          </ul>
          {bulkKnownCompanies} + {bulkMissingDetail.length} missing = {bulkListSize} companies on the list.
        </Alert>
      ) : null}
      {bulkMissingDetail.length ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          {bulkMissingDetail.length} of the {bulkListSize} listed companies produced no row here:
          <ul style={{ margin: "6px 0 0", paddingLeft: 20 }}>
            {bulkMissingDetail.map((m) => (
              <li key={m.name} style={{ marginBottom: 4 }}>
                <strong>{m.name}</strong>
                {m.onPortal && m.onPortal.length ? (
                  <>
                    {" "}— <strong>on the Ingram portal</strong>:{" "}
                    {m.onPortal
                      .map(
                        (p) =>
                          `${p.ingram_subscription_id} ${p.status}${p.licenses ? `, ${p.licenses} licence(s)` : ""}${p.term_billing ? `, ${p.term_billing}` : ""}`
                      )
                      .join("; ")}
                    . Not in the order sweep — re-run Full sync to pick it up.
                  </>
                ) : m.where && m.where.length ? (
                  <> — found as: {m.where.map((w) => `${w.status}${w.flag ? ` (${w.flag})` : ""} on ${w.plan || "unknown plan"}`).join("; ")}</>
                ) : m.near && m.near.length ? (
                  <>
                    {" "}— not under this exact name, but Ingram has a very similar one:{" "}
                    {m.near.map((w) => (
                      <span key={w.ingram_subscription_id}>
                        <strong>{w.customer}</strong> (sub {w.ingram_subscription_id}, {w.status}
                        {w.flag ? `, ${w.flag}` : ""})
                      </span>
                    ))}
                    . The spelling differs, so the exact match skipped it — fix the name in the CSV or accept it as-is.
                  </>
                ) : (
                  " — no Ingram subscription under this name, and nothing close to it either."
                )}
              </li>
            ))}
          </ul>
        </Alert>
      ) : null}
      <FlaggedTable rows={flaggedBulkKnown} onLink={setLinkRow} />

      <LinkHowTo row={linkRow} onClose={() => setLinkRow(null)} />

      {/* ---- Per-plan mapping cheat-sheet ---- */}
      <Dialog open={!!infoPlan} onClose={() => setInfoPlan(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 0 }}>
          Ingram auto-fulfilment values
          <Typography variant="body2" color="text.secondary">{infoPlan?.plan_name}</Typography>
        </DialogTitle>
        <DialogContent>
          {(() => {
            if (!infoPlan) return null;
            const m = infoPlan.mapping || {};
            const isGoogle = String(m.provider || "").toLowerCase() === "google";
            const rows = isGoogle
              ? [
                  ["Provider", m.provider],
                  ["Fulfilment", m.fulfilment],
                  ["Google product URL (subscriptionoptions)", m.product_url],
                ]
              : [
                  ["Provider", m.provider],
                  ["Fulfilment", m.fulfilment],
                  ["Plan ID", m.plan_id],
                  ["Vendor Part Number (VPN)", m.vendor_part_number],
                  ["Resource ID", m.resource_id],
                  ["Product ID", m.product_id],
                ];
            const portalUrl = m.product_url || infoPlan.product_url || null;
            const term = infoPlan.term || null;
            const missing = rows.filter(([, v]) => !v).map(([k]) => k);
            const src = infoPlan.idSource || {};
            const productLevel = Object.entries(src).filter(([, v]) => v === "product").map(([k]) => k);
            const blob = rows.filter(([, v]) => v).map(([k, v]) => `${k}: ${v}`).join("\n");
            return (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Paste these into this product&apos;s <strong>Ingram auto-fulfilment</strong> card in the price editor,
                  then tick <strong>Active</strong> and Save mapping.
                  {isGoogle ? " For the Flexible (usage-based) plan also tick that checkbox." : ""}
                </Typography>
                <Table size="small">
                  <TableBody>
                    {rows.map(([k, v]) => (
                      <TableRow key={k}>
                        <TableCell sx={{ width: 260, fontWeight: 500, borderBottom: "none" }}>{k}</TableCell>
                        <TableCell sx={{ fontFamily: "monospace", wordBreak: "break-all", borderBottom: "none" }}>
                          {v || <span style={{ color: "#b26a00" }}>— not resolved —</span>}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {term ? (
                  <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Commitment term:
                    </Typography>
                    <Chip size="small" color="primary" variant="outlined" label={term.label} />
                    <Typography variant="caption" color="text.secondary">
                      map this plan to the price whose validity is {term.label.toLowerCase()}
                    </Typography>
                  </Box>
                ) : null}
                {portalUrl ? (
                  <Box sx={{ mt: 2 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      component="a"
                      href={portalUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Open on Ingram portal
                    </Button>
                  </Box>
                ) : m.vendor_part_number ? (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    To open this product on the portal, paste its VPN{" "}
                    <strong style={{ fontFamily: "monospace" }}>{m.vendor_part_number}</strong> into the Ingram search
                    box. The Ingram API does not expose the portal page URL, so we don&apos;t guess one — a guessed link
                    opens the wrong product.
                  </Alert>
                ) : null}
                {missing.length ? (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    Not resolved from the Ingram catalog: <strong>{missing.join(", ")}</strong>. Copy these from the
                    Ingram portal for this plan before saving — don&apos;t guess them.
                  </Alert>
                ) : null}
                {productLevel.length ? (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    <strong>{productLevel.join(", ")}</strong> came from the parent product (shared by sibling plans),
                    not the plan itself — verify against Ingram for this exact plan+term.
                  </Alert>
                ) : null}
                {blob ? (
                  <Box
                    component="pre"
                    sx={{ mt: 2, p: 1.5, bgcolor: "#f6f8fa", borderRadius: 1, fontSize: 12, whiteSpace: "pre-wrap", wordBreak: "break-all" }}
                  >
                    {blob}
                  </Box>
                ) : null}
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              const m = (infoPlan && infoPlan.mapping) || {};
              const txt = Object.entries(m)
                .filter(([, v]) => v)
                .map(([k, v]) => `${k}: ${v}`)
                .join("\n");
              if (navigator.clipboard && txt) navigator.clipboard.writeText(txt);
            }}
          >
            Copy all
          </Button>
          <Button onClick={() => setInfoPlan(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
